import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Heading } from "src/components/base/heading";
import { Button } from "src/components/base/button";
import { Input } from "src/components/base/input";
import { Select } from "src/components/base/select";
import { Field, Label, Fieldset, FieldGroup } from "src/components/base/fieldset";
import { Switch, SwitchField } from "src/components/base/switch";
import { Badge } from "src/components/base/badge";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "src/components/base/table";
import { Dialog, DialogTitle, DialogBody, DialogActions } from "src/components/base/dialog";
import { MOCK_PROFILE } from "../data/settings";
import { Card, StatCard } from "../shared";
import {
    CameraIcon,
    ComputerDesktopIcon,
    DevicePhoneMobileIcon,
    DeviceTabletIcon,
    KeyIcon,
    TrashIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    XMarkIcon,
    ClipboardDocumentIcon,
    ArrowPathIcon,
    SunIcon,
    MoonIcon,
    SwatchIcon,
    GlobeAltIcon,
    ShieldCheckIcon,
    LinkIcon,
} from "@heroicons/react/20/solid";

// ---------------------------------------------------------------------------
// Types & mock data
// ---------------------------------------------------------------------------

interface ActiveSession {
    id: string;
    device: string;
    deviceType: "desktop" | "mobile" | "tablet";
    browser: string;
    location: string;
    ip: string;
    lastActive: string;
    current: boolean;
}

interface LoginEvent {
    id: string;
    timestamp: string;
    ip: string;
    device: string;
    location: string;
    success: boolean;
}

interface ApiKey {
    id: string;
    name: string;
    createdAt: string;
    lastUsed: string;
    partialKey: string;
    scopes: string[];
    active: boolean;
}

interface ConnectedIntegration {
    id: string;
    name: string;
    icon: string;
    description: string;
    connected: boolean;
    lastSync: string | null;
    accountName: string | null;
}

type ThemeOption = "light" | "dark" | "auto";
type AccentColor = "blue" | "purple" | "green" | "orange";

interface NotificationPreference {
    eventType: string;
    email: boolean;
    push: boolean;
    sms: boolean;
}

// --- Mock data ---

const MOCK_SESSIONS: ActiveSession[] = [
    {
        id: "sess-1",
        device: 'MacBook Pro 16"',
        deviceType: "desktop",
        browser: "Chrome 122",
        location: "Berlin, DE",
        ip: "203.0.113.42",
        lastActive: "Just now",
        current: true,
    },
    {
        id: "sess-2",
        device: "iPhone 15 Pro",
        deviceType: "mobile",
        browser: "Safari 17",
        location: "Berlin, DE",
        ip: "203.0.113.42",
        lastActive: "2 hours ago",
        current: false,
    },
    {
        id: "sess-3",
        device: "iPad Air",
        deviceType: "tablet",
        browser: "Safari 17",
        location: "Munich, DE",
        ip: "198.51.100.7",
        lastActive: "1 day ago",
        current: false,
    },
];

const MOCK_LOGIN_EVENTS: LoginEvent[] = [
    {
        id: "login-1",
        timestamp: "2026-03-13 14:23:01",
        ip: "203.0.113.42",
        device: "Chrome / macOS",
        location: "Berlin, DE",
        success: true,
    },
    {
        id: "login-2",
        timestamp: "2026-03-13 08:15:33",
        ip: "203.0.113.42",
        device: "Safari / iOS",
        location: "Berlin, DE",
        success: true,
    },
    {
        id: "login-3",
        timestamp: "2026-03-12 22:47:12",
        ip: "198.51.100.7",
        device: "Safari / iPadOS",
        location: "Munich, DE",
        success: true,
    },
    {
        id: "login-4",
        timestamp: "2026-03-12 19:03:44",
        ip: "192.0.2.99",
        device: "Firefox / Windows",
        location: "Unknown",
        success: false,
    },
    {
        id: "login-5",
        timestamp: "2026-03-12 14:11:08",
        ip: "203.0.113.42",
        device: "Chrome / macOS",
        location: "Berlin, DE",
        success: true,
    },
];

const MOCK_API_KEYS: ApiKey[] = [
    {
        id: "key-1",
        name: "CI/CD Pipeline",
        createdAt: "2025-11-01",
        lastUsed: "2026-03-13",
        partialKey: "tp_live_****...a3f2",
        scopes: ["read:alerts", "write:tickets"],
        active: true,
    },
    {
        id: "key-2",
        name: "SIEM Integration",
        createdAt: "2025-12-15",
        lastUsed: "2026-03-12",
        partialKey: "tp_live_****...b7d1",
        scopes: ["read:alerts", "read:events"],
        active: true,
    },
    {
        id: "key-3",
        name: "Test Key (Staging)",
        createdAt: "2026-01-20",
        lastUsed: "2026-02-28",
        partialKey: "tp_test_****...c9e4",
        scopes: ["read:alerts"],
        active: false,
    },
];

const MOCK_INTEGRATIONS: ConnectedIntegration[] = [
    {
        id: "int-slack",
        name: "Slack",
        icon: "S",
        description: "Send notifications to Slack channels",
        connected: true,
        lastSync: "2 min ago",
        accountName: "TrufflePig Workspace",
    },
    {
        id: "int-jira",
        name: "Jira",
        icon: "J",
        description: "Create and sync security tickets",
        connected: true,
        lastSync: "5 min ago",
        accountName: "trufflepig.atlassian.net",
    },
    {
        id: "int-pagerduty",
        name: "PagerDuty",
        icon: "P",
        description: "Trigger on-call escalations",
        connected: false,
        lastSync: null,
        accountName: null,
    },
];

const MOCK_NOTIFICATION_PREFS: NotificationPreference[] = [
    { eventType: "Critical Alert", email: true, push: true, sms: true },
    { eventType: "High Alert", email: true, push: true, sms: false },
    { eventType: "Pentest Finding", email: true, push: false, sms: false },
    { eventType: "Weekly Report", email: true, push: false, sms: false },
    { eventType: "System Maintenance", email: false, push: true, sms: false },
    { eventType: "Policy Change", email: true, push: true, sms: false },
];

const ACCENT_COLORS: { key: AccentColor; label: string; tw: string; ring: string }[] = [
    { key: "blue", label: "Blue", tw: "bg-blue-500", ring: "ring-blue-500" },
    { key: "purple", label: "Purple", tw: "bg-purple-500", ring: "ring-purple-500" },
    { key: "green", label: "Green", tw: "bg-green-500", ring: "ring-green-500" },
    { key: "orange", label: "Orange", tw: "bg-orange-500", ring: "ring-orange-500" },
];

// ---------------------------------------------------------------------------
// Helper sub-components
// ---------------------------------------------------------------------------

function DeviceIcon({ type }: { type: ActiveSession["deviceType"] }) {
    switch (type) {
        case "desktop":
            return <ComputerDesktopIcon className="h-5 w-5 text-zinc-400" />;
        case "mobile":
            return <DevicePhoneMobileIcon className="h-5 w-5 text-zinc-400" />;
        case "tablet":
            return <DeviceTabletIcon className="h-5 w-5 text-zinc-400" />;
    }
}

function SectionHeading({
    children,
    icon: Icon,
    description,
}: {
    children: React.ReactNode;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    description?: string;
}) {
    return (
        <div className="mb-4">
            <div className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-blue-500" />
                <h3 className="text-base font-semibold text-zinc-900 dark:text-white">{children}</h3>
            </div>
            {description && <p className="ml-7 mt-1 text-xs text-zinc-500 dark:text-zinc-400">{description}</p>}
        </div>
    );
}

function ThemePreviewCard({ theme, active, onClick }: { theme: ThemeOption; active: boolean; onClick: () => void }) {
    const bgClasses: Record<ThemeOption, string> = {
        light: "bg-white border-zinc-200",
        dark: "bg-zinc-900 border-zinc-700",
        auto: "bg-gradient-to-r from-white to-zinc-900 border-zinc-400",
    };
    const labelClasses: Record<ThemeOption, string> = {
        light: "text-zinc-900",
        dark: "text-white",
        auto: "text-zinc-600 dark:text-zinc-300",
    };
    const icons: Record<ThemeOption, React.ReactNode> = {
        light: <SunIcon className="h-6 w-6 text-amber-500" />,
        dark: <MoonIcon className="h-6 w-6 text-indigo-400" />,
        auto: <ComputerDesktopIcon className="h-6 w-6 text-zinc-500" />,
    };

    return (
        <button
            onClick={onClick}
            className={`relative flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-all duration-200 ${bgClasses[theme]} ${active ? "ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-zinc-900" : "opacity-60 hover:scale-105 hover:opacity-100"}`}
        >
            {active && <CheckCircleIcon className="absolute -right-2 -top-2 h-5 w-5 text-blue-500" />}
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-zinc-100/20 backdrop-blur-sm">
                {icons[theme]}
            </div>
            <span className={`text-xs font-semibold capitalize ${labelClasses[theme]}`}>{theme}</span>
            <div className={`h-8 w-full rounded-md ${bgClasses[theme]} border`}>
                <div className="flex h-full items-center gap-1 px-2">
                    <div className="h-1.5 w-4 rounded-full bg-zinc-400/30" />
                    <div className="h-1.5 w-6 rounded-full bg-zinc-400/30" />
                    <div className="h-1.5 w-3 rounded-full bg-zinc-400/30" />
                </div>
            </div>
        </button>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SettingsProfile() {
    const { t } = useTranslation();
    const [profile, setProfile] = useState(MOCK_PROFILE);

    // Avatar
    const [avatarHover, setAvatarHover] = useState(false);
    const [avatarUrl] = useState<string | null>(null);

    // Security / password
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordStrength, setPasswordStrength] = useState(0);

    // Sessions
    const [sessions, setSessions] = useState<ActiveSession[]>(MOCK_SESSIONS);

    // Notification prefs
    const [notifPrefs, setNotifPrefs] = useState<NotificationPreference[]>(MOCK_NOTIFICATION_PREFS);

    // API Keys
    const [apiKeys, setApiKeys] = useState<ApiKey[]>(MOCK_API_KEYS);
    const [showNewKey, setShowNewKey] = useState(false);
    const [newKeyName, setNewKeyName] = useState("");
    const [generatedKey, setGeneratedKey] = useState<string | null>(null);

    // Integrations
    const [integrations, setIntegrations] = useState<ConnectedIntegration[]>(MOCK_INTEGRATIONS);

    // Appearance
    const [theme, setTheme] = useState<ThemeOption>("auto");
    const [accentColor, setAccentColor] = useState<AccentColor>("blue");

    // Delete account dialog
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");

    // Activity log
    const [loginEvents] = useState<LoginEvent[]>(MOCK_LOGIN_EVENTS);

    // --- Callbacks ---

    const computePasswordStrength = useCallback((pw: string) => {
        let score = 0;
        if (pw.length >= 8) score += 25;
        if (pw.length >= 12) score += 15;
        if (/[A-Z]/.test(pw)) score += 15;
        if (/[a-z]/.test(pw)) score += 10;
        if (/[0-9]/.test(pw)) score += 15;
        if (/[^A-Za-z0-9]/.test(pw)) score += 20;
        return Math.min(score, 100);
    }, []);

    const handleNewPasswordChange = useCallback(
        (val: string) => {
            setNewPassword(val);
            setPasswordStrength(computePasswordStrength(val));
        },
        [computePasswordStrength],
    );

    const revokeSession = useCallback((sessionId: string) => {
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    }, []);

    const toggleNotifPref = useCallback((eventType: string, channel: "email" | "push" | "sms") => {
        setNotifPrefs((prev) => prev.map((p) => (p.eventType === eventType ? { ...p, [channel]: !p[channel] } : p)));
    }, []);

    const revokeApiKey = useCallback((keyId: string) => {
        setApiKeys((prev) => prev.filter((k) => k.id !== keyId));
    }, []);

    const generateNewKey = useCallback(() => {
        const mockKey = `tp_live_${Array.from({ length: 32 }, () => "abcdef0123456789"[Math.floor(Math.random() * 16)]).join("")}`;
        const newKey: ApiKey = {
            id: `key-${Date.now()}`,
            name: newKeyName || "Unnamed Key",
            createdAt: new Date().toISOString().split("T")[0],
            lastUsed: "Never",
            partialKey: `tp_live_****...${mockKey.slice(-4)}`,
            scopes: ["read:alerts"],
            active: true,
        };
        setApiKeys((prev) => [...prev, newKey]);
        setGeneratedKey(mockKey);
    }, [newKeyName]);

    const toggleIntegration = useCallback((integrationId: string) => {
        setIntegrations((prev) =>
            prev.map((int) =>
                int.id === integrationId
                    ? {
                          ...int,
                          connected: !int.connected,
                          lastSync: !int.connected ? "Just now" : null,
                          accountName: !int.connected ? "Connected" : null,
                      }
                    : int,
            ),
        );
    }, []);

    const passwordStrengthLabel = useMemo(() => {
        if (passwordStrength === 0) return "";
        if (passwordStrength < 30) return "Weak";
        if (passwordStrength < 60) return "Fair";
        if (passwordStrength < 80) return "Good";
        return "Strong";
    }, [passwordStrength]);

    const passwordStrengthColor = useMemo(() => {
        if (passwordStrength < 30) return "bg-red-500";
        if (passwordStrength < 60) return "bg-yellow-500";
        if (passwordStrength < 80) return "bg-blue-500";
        return "bg-green-500";
    }, [passwordStrength]);

    const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;
    const passwordsMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

    const initials = profile.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase();

    return (
        <>
            <Heading>{t("settings.profile.title")}</Heading>
            <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">{t("settings.profile.description")}</p>

            {/* ============================================================ */}
            {/* AVATAR + BASIC PROFILE                                       */}
            {/* ============================================================ */}
            <Card className="mt-6 max-w-3xl">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                    {/* Avatar upload */}
                    <div className="flex flex-col items-center gap-2">
                        <div
                            className="relative h-24 w-24 cursor-pointer overflow-hidden rounded-full border-4 border-white shadow-lg dark:border-zinc-700"
                            onMouseEnter={() => setAvatarHover(true)}
                            onMouseLeave={() => setAvatarHover(false)}
                        >
                            {avatarUrl ? (
                                <img src={avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 text-2xl font-bold text-white">
                                    {initials}
                                </div>
                            )}
                            {avatarHover && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity">
                                    <CameraIcon className="h-6 w-6 text-white" />
                                </div>
                            )}
                        </div>
                        <span className="text-xs text-zinc-400">Click to upload</span>
                    </div>

                    {/* Profile fields */}
                    <div className="flex-1">
                        <Fieldset>
                            <FieldGroup>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <Field>
                                        <Label>{t("settings.profile.name")}</Label>
                                        <Input
                                            value={profile.name}
                                            onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                        />
                                    </Field>
                                    <Field>
                                        <Label>{t("settings.profile.email")}</Label>
                                        <Input value={profile.email} disabled />
                                    </Field>
                                    <Field>
                                        <Label>{t("settings.profile.role")}</Label>
                                        <Input value={profile.role} disabled />
                                    </Field>
                                    <Field>
                                        <Label>{t("settings.profile.timezone")}</Label>
                                        <Select
                                            value={profile.timezone}
                                            onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                                        >
                                            <option value="Europe/Berlin">Europe/Berlin (CET)</option>
                                            <option value="Europe/London">Europe/London (GMT)</option>
                                            <option value="America/New_York">America/New York (EST)</option>
                                            <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                                            <option value="UTC">UTC</option>
                                        </Select>
                                    </Field>
                                    <Field>
                                        <Label>{t("settings.profile.language")}</Label>
                                        <Select
                                            value={profile.language}
                                            onChange={(e) => setProfile({ ...profile, language: e.target.value })}
                                        >
                                            <option value="de">Deutsch</option>
                                            <option value="en">English</option>
                                        </Select>
                                    </Field>
                                </div>
                                <SwitchField>
                                    <Label>{t("settings.profile.twoFactor")}</Label>
                                    <Switch
                                        checked={profile.twoFactor}
                                        onChange={(checked) => setProfile({ ...profile, twoFactor: checked })}
                                    />
                                </SwitchField>
                            </FieldGroup>
                        </Fieldset>
                        <div className="mt-6">
                            <Button color="blue">{t("settings.profile.save")}</Button>
                        </div>
                    </div>
                </div>
            </Card>

            {/* ============================================================ */}
            {/* SECURITY: PASSWORD CHANGE                                    */}
            {/* ============================================================ */}
            <Card className="mt-6 max-w-3xl">
                <SectionHeading icon={ShieldCheckIcon} description="Change your password and manage security settings">
                    Security
                </SectionHeading>
                <Fieldset>
                    <FieldGroup>
                        <Field>
                            <Label>Current Password</Label>
                            <Input
                                type="password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                placeholder="Enter current password"
                            />
                        </Field>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field>
                                <Label>New Password</Label>
                                <Input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => handleNewPasswordChange(e.target.value)}
                                    placeholder="Enter new password"
                                />
                                {newPassword.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                        <div className="flex items-center gap-2">
                                            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
                                                <div
                                                    className={`h-full rounded-full transition-all duration-300 ${passwordStrengthColor}`}
                                                    style={{ width: `${passwordStrength}%` }}
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-zinc-500">
                                                {passwordStrengthLabel}
                                            </span>
                                        </div>
                                        <ul className="space-y-0.5 text-[10px] text-zinc-400">
                                            <li className={newPassword.length >= 8 ? "text-green-500" : ""}>
                                                {newPassword.length >= 8 ? "✓" : "○"} At least 8 characters
                                            </li>
                                            <li className={/[A-Z]/.test(newPassword) ? "text-green-500" : ""}>
                                                {/[A-Z]/.test(newPassword) ? "✓" : "○"} Uppercase letter
                                            </li>
                                            <li className={/[0-9]/.test(newPassword) ? "text-green-500" : ""}>
                                                {/[0-9]/.test(newPassword) ? "✓" : "○"} Number
                                            </li>
                                            <li className={/[^A-Za-z0-9]/.test(newPassword) ? "text-green-500" : ""}>
                                                {/[^A-Za-z0-9]/.test(newPassword) ? "✓" : "○"} Special character
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </Field>
                            <Field>
                                <Label>Confirm New Password</Label>
                                <Input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                />
                                {passwordsMatch && (
                                    <p className="mt-1 flex items-center gap-1 text-xs text-green-500">
                                        <CheckCircleIcon className="h-3.5 w-3.5" /> Passwords match
                                    </p>
                                )}
                                {passwordsMismatch && (
                                    <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
                                        <XMarkIcon className="h-3.5 w-3.5" /> Passwords do not match
                                    </p>
                                )}
                            </Field>
                        </div>
                    </FieldGroup>
                </Fieldset>
                <div className="mt-4">
                    <Button color="blue" disabled={!passwordsMatch || passwordStrength < 30}>
                        Update Password
                    </Button>
                </div>
            </Card>

            {/* ============================================================ */}
            {/* ACTIVE SESSIONS                                              */}
            {/* ============================================================ */}
            <Card className="mt-6 max-w-3xl">
                <SectionHeading icon={ComputerDesktopIcon} description="Devices currently signed in to your account">
                    Active Sessions ({sessions.length})
                </SectionHeading>
                <div className="space-y-3">
                    {sessions.map((session) => (
                        <div
                            key={session.id}
                            className={`flex items-center gap-4 rounded-xl border p-4 transition-all ${session.current ? "border-blue-200 bg-blue-50/50 dark:border-blue-800/50 dark:bg-blue-950/20" : "border-zinc-200/60 dark:border-zinc-700/40"}`}
                        >
                            <DeviceIcon type={session.deviceType} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-zinc-900 dark:text-white">
                                        {session.device}
                                    </span>
                                    {session.current && <Badge color="blue">Current</Badge>}
                                </div>
                                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-zinc-500">
                                    <span>{session.browser}</span>
                                    <span className="font-mono">{session.ip}</span>
                                    <span>{session.location}</span>
                                    <span>{session.lastActive}</span>
                                </div>
                            </div>
                            {!session.current && (
                                <Button plain onClick={() => revokeSession(session.id)}>
                                    <XMarkIcon className="h-4 w-4 text-red-500" />
                                    Revoke
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
            </Card>

            {/* ============================================================ */}
            {/* NOTIFICATION PREFERENCES PER EVENT TYPE                      */}
            {/* ============================================================ */}
            <Card className="mt-6 max-w-3xl">
                <SectionHeading icon={GlobeAltIcon} description="Control how you are notified for each event type">
                    Notification Preferences
                </SectionHeading>
                <Table dense>
                    <TableHead>
                        <TableRow>
                            <TableHeader>Event Type</TableHeader>
                            <TableHeader className="text-center">Email</TableHeader>
                            <TableHeader className="text-center">Push</TableHeader>
                            <TableHeader className="text-center">SMS</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notifPrefs.map((pref) => (
                            <TableRow key={pref.eventType}>
                                <TableCell className="text-sm font-medium">{pref.eventType}</TableCell>
                                <TableCell className="text-center">
                                    <Switch
                                        checked={pref.email}
                                        onChange={() => toggleNotifPref(pref.eventType, "email")}
                                    />
                                </TableCell>
                                <TableCell className="text-center">
                                    <Switch
                                        checked={pref.push}
                                        onChange={() => toggleNotifPref(pref.eventType, "push")}
                                    />
                                </TableCell>
                                <TableCell className="text-center">
                                    <Switch
                                        checked={pref.sms}
                                        onChange={() => toggleNotifPref(pref.eventType, "sms")}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>

            {/* ============================================================ */}
            {/* API KEYS                                                     */}
            {/* ============================================================ */}
            <Card className="mt-6 max-w-3xl">
                <div className="flex items-center justify-between">
                    <SectionHeading icon={KeyIcon} description="Manage API keys for programmatic access">
                        API Keys
                    </SectionHeading>
                    <Button
                        color="blue"
                        onClick={() => {
                            setShowNewKey(true);
                            setGeneratedKey(null);
                            setNewKeyName("");
                        }}
                    >
                        <KeyIcon className="h-4 w-4" />
                        Generate New Key
                    </Button>
                </div>
                <Table dense className="mt-2">
                    <TableHead>
                        <TableRow>
                            <TableHeader>Name</TableHeader>
                            <TableHeader>Key</TableHeader>
                            <TableHeader>Created</TableHeader>
                            <TableHeader>Last Used</TableHeader>
                            <TableHeader>Scopes</TableHeader>
                            <TableHeader>Status</TableHeader>
                            <TableHeader></TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {apiKeys.map((key) => (
                            <TableRow key={key.id}>
                                <TableCell className="font-medium">{key.name}</TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-1">
                                        <span className="font-mono text-xs text-zinc-500">{key.partialKey}</span>
                                        <button className="text-zinc-400 hover:text-zinc-600" title="Copy">
                                            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </TableCell>
                                <TableCell className="font-mono text-xs text-zinc-500">{key.createdAt}</TableCell>
                                <TableCell className="font-mono text-xs text-zinc-500">{key.lastUsed}</TableCell>
                                <TableCell>
                                    <div className="flex flex-wrap gap-1">
                                        {key.scopes.map((scope) => (
                                            <Badge key={scope} color="zinc">
                                                {scope}
                                            </Badge>
                                        ))}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Badge color={key.active ? "green" : "zinc"}>
                                        {key.active ? "Active" : "Inactive"}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <Button plain onClick={() => revokeApiKey(key.id)}>
                                        <TrashIcon className="h-4 w-4 text-red-500" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>

            {/* ============================================================ */}
            {/* CONNECTED INTEGRATIONS                                       */}
            {/* ============================================================ */}
            <Card className="mt-6 max-w-3xl">
                <SectionHeading icon={LinkIcon} description="Connect third-party services to your account">
                    Connected Integrations
                </SectionHeading>
                <div className="space-y-3">
                    {integrations.map((integration) => (
                        <div
                            key={integration.id}
                            className="flex items-center gap-4 rounded-xl border border-zinc-200/60 p-4 dark:border-zinc-700/40"
                        >
                            <div
                                className={`flex h-10 w-10 items-center justify-center rounded-lg font-bold text-white ${integration.connected ? "bg-gradient-to-br from-blue-500 to-blue-600" : "bg-zinc-400"}`}
                            >
                                {integration.icon}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-zinc-900 dark:text-white">
                                        {integration.name}
                                    </span>
                                    {integration.connected && <Badge color="green">Connected</Badge>}
                                </div>
                                <p className="text-xs text-zinc-500">{integration.description}</p>
                                {integration.connected && integration.accountName && (
                                    <div className="mt-0.5 flex items-center gap-2 text-xs text-zinc-400">
                                        <span>{integration.accountName}</span>
                                        <span className="flex items-center gap-0.5">
                                            <ArrowPathIcon className="h-3 w-3" />
                                            {integration.lastSync}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <Switch
                                checked={integration.connected}
                                onChange={() => toggleIntegration(integration.id)}
                            />
                        </div>
                    ))}
                </div>
            </Card>

            {/* ============================================================ */}
            {/* APPEARANCE                                                   */}
            {/* ============================================================ */}
            <Card className="mt-6 max-w-3xl">
                <SectionHeading icon={SwatchIcon} description="Customize the look and feel of your dashboard">
                    Appearance
                </SectionHeading>

                <div className="space-y-6">
                    {/* Theme */}
                    <div>
                        <p className="mb-3 text-sm font-medium text-zinc-700 dark:text-zinc-300">Theme</p>
                        <div className="grid grid-cols-3 gap-4">
                            {(["light", "dark", "auto"] as ThemeOption[]).map((t) => (
                                <ThemePreviewCard key={t} theme={t} active={theme === t} onClick={() => setTheme(t)} />
                            ))}
                        </div>
                    </div>

                    {/* Accent color */}
                    <div>
                        <p className="mb-3 text-sm font-medium text-zinc-700 dark:text-zinc-300">Accent Color</p>
                        <div className="flex gap-3">
                            {ACCENT_COLORS.map((ac) => (
                                <button
                                    key={ac.key}
                                    onClick={() => setAccentColor(ac.key)}
                                    className={`flex flex-col items-center gap-1.5 rounded-lg p-2 transition-all ${accentColor === ac.key ? "scale-110" : "opacity-60 hover:opacity-100"}`}
                                >
                                    <div
                                        className={`h-8 w-8 rounded-full ${ac.tw} ${accentColor === ac.key ? `ring-2 ${ac.ring} ring-offset-2 dark:ring-offset-zinc-900` : ""} shadow-md transition-all`}
                                    />
                                    <span className="text-[10px] font-medium text-zinc-500">{ac.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </Card>

            {/* ============================================================ */}
            {/* ACTIVITY LOG                                                 */}
            {/* ============================================================ */}
            <Card className="mt-6 max-w-3xl">
                <SectionHeading icon={GlobeAltIcon} description="Recent authentication events for your account">
                    Activity Log
                </SectionHeading>
                <Table dense>
                    <TableHead>
                        <TableRow>
                            <TableHeader>Timestamp</TableHeader>
                            <TableHeader>IP Address</TableHeader>
                            <TableHeader>Device</TableHeader>
                            <TableHeader>Location</TableHeader>
                            <TableHeader>Status</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loginEvents.map((event) => (
                            <TableRow key={event.id}>
                                <TableCell className="font-mono text-xs">{event.timestamp}</TableCell>
                                <TableCell className="font-mono text-xs">{event.ip}</TableCell>
                                <TableCell className="text-xs">{event.device}</TableCell>
                                <TableCell className="text-xs">{event.location}</TableCell>
                                <TableCell>
                                    <Badge color={event.success ? "green" : "red"}>
                                        {event.success ? "Success" : "Failed"}
                                    </Badge>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>

            {/* ============================================================ */}
            {/* DANGER ZONE                                                  */}
            {/* ============================================================ */}
            <Card className="mt-6 max-w-3xl border-red-200/60 dark:border-red-900/40">
                <SectionHeading icon={ExclamationTriangleIcon} description="Irreversible and destructive actions">
                    Danger Zone
                </SectionHeading>
                <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50/50 p-4 dark:border-red-900/50 dark:bg-red-950/20">
                    <div>
                        <p className="text-sm font-medium text-red-900 dark:text-red-300">Delete Account</p>
                        <p className="text-xs text-red-700/70 dark:text-red-400/70">
                            Permanently delete your account and all associated data. This action cannot be undone.
                        </p>
                    </div>
                    <Button color="red" onClick={() => setShowDeleteDialog(true)}>
                        <TrashIcon className="h-4 w-4" />
                        Delete Account
                    </Button>
                </div>
            </Card>

            {/* ============================================================ */}
            {/* DIALOGS                                                      */}
            {/* ============================================================ */}

            {/* Generate new API key dialog */}
            <Dialog open={showNewKey} onClose={() => setShowNewKey(false)} size="lg">
                <DialogTitle>Generate New API Key</DialogTitle>
                <DialogBody>
                    {generatedKey ? (
                        <div className="space-y-4">
                            <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800/50 dark:bg-green-950/20">
                                <p className="mb-2 text-sm font-medium text-green-800 dark:text-green-300">
                                    Your new API key (copy it now, it will not be shown again):
                                </p>
                                <div className="flex items-center gap-2 rounded-md bg-white p-2 font-mono text-xs dark:bg-zinc-900">
                                    <span className="flex-1 break-all">{generatedKey}</span>
                                    <button className="text-zinc-400 hover:text-zinc-600">
                                        <ClipboardDocumentIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <Field>
                                <Label>Key Name</Label>
                                <Input
                                    value={newKeyName}
                                    onChange={(e) => setNewKeyName(e.target.value)}
                                    placeholder="e.g. CI/CD Pipeline"
                                />
                            </Field>
                            <Field>
                                <Label>Scopes</Label>
                                <div className="mt-1 flex flex-wrap gap-2">
                                    {[
                                        "read:alerts",
                                        "write:alerts",
                                        "read:events",
                                        "write:tickets",
                                        "read:reports",
                                        "admin",
                                    ].map((scope) => (
                                        <label
                                            key={scope}
                                            className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-zinc-200 px-2.5 py-1.5 text-xs transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
                                        >
                                            <input
                                                type="checkbox"
                                                className="rounded"
                                                defaultChecked={scope === "read:alerts"}
                                            />
                                            {scope}
                                        </label>
                                    ))}
                                </div>
                            </Field>
                            <Field>
                                <Label>Expiration</Label>
                                <Select>
                                    <option value="30">30 days</option>
                                    <option value="90">90 days</option>
                                    <option value="365">1 year</option>
                                    <option value="never">Never</option>
                                </Select>
                            </Field>
                        </div>
                    )}
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setShowNewKey(false)}>
                        {generatedKey ? "Close" : "Cancel"}
                    </Button>
                    {!generatedKey && (
                        <Button color="blue" onClick={generateNewKey}>
                            Generate Key
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Delete account dialog */}
            <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} size="lg">
                <DialogTitle>Delete Account</DialogTitle>
                <DialogBody>
                    <div className="space-y-4">
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800/50 dark:bg-red-950/20">
                            <p className="text-sm text-red-800 dark:text-red-300">
                                This will permanently delete your account, all API keys, integrations, and associated
                                data. This action is irreversible.
                            </p>
                        </div>
                        <Field>
                            <Label>Type "DELETE" to confirm</Label>
                            <Input
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                placeholder="DELETE"
                            />
                        </Field>
                    </div>
                </DialogBody>
                <DialogActions>
                    <Button
                        plain
                        onClick={() => {
                            setShowDeleteDialog(false);
                            setDeleteConfirmText("");
                        }}
                    >
                        Cancel
                    </Button>
                    <Button color="red" disabled={deleteConfirmText !== "DELETE"}>
                        <TrashIcon className="h-4 w-4" />
                        Delete My Account
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
